/**
 * ScopeSidebar — recursive, dimension-agnostic navigation sidebar.
 *
 * Renders a tree of dimension folders. At each level of the scope path,
 * the selected entry is expanded to reveal the remaining (unused) dimensions
 * filtered to that scope. Every entry shows a sparkline and share bar
 * relative to the current-level trace set.
 *
 * All trace data and computation lives in the Web Worker. This component
 * only receives pre-computed entries via useQuery.
 */

import { createContext, Fragment, memo, startTransition, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import StickyFolder, { FolderList, FolderToolbar, LoadingSkeleton } from "./StickyFolder.jsx";
import FilterBuilder from "./FilterBuilder.jsx";
import { STEER_FIELDS, parseQuery, serializeQuery } from "../lib/queryLang.js";
import { andQueriesAll } from "../lib/queryPipeline.js";
import { CACHE_NOTIFY, PaginatedExplorerContent, markTrailExpansion, rowChromeClass, useCachedState } from "./SidebarNavRuntime.jsx";
import { DIMENSION_LIST, DIMENSIONS, encodeScopePath, canMultiSelect, stepIds, passesTenantAvailability, dimensionSentenceLabel } from "../lib/dimensionDefs.js";
import { phAttrs } from "../lib/analytics/attrs.js";
import { ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";
import { appLenses } from "../lib/lenses/appLenses.js";
import { defaultLensOf } from "../lib/lenses/lensRegistry.js";
import { useQuery, usePagedDimEntries, useCurrentRange } from "../lib/dataClient.js";
import { applyEndpointDedupe } from "../lib/endpointDedupe.js";
import { formatCompact } from "../lib/renderers.js";
import { EYEBROW_CLASS, ROOT_NAV_TITLE_CLASS, ROOT_NAV_META_CLASS } from "../lib/ui.js";
import { DimensionIcon, DimensionLabel, DimensionSecondary } from "./Dimension.jsx";
import { analyticsErrorCopy } from "../lib/analyticsClient.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { DENIAL_REASON, denialCopy } from "../lib/denialReason.js";

// ── Lens vs. dimension legibility ─────────────────────────────────
//
// Two kinds of rows render at every scope: scope/value *lenses* (Overview,
// X-ray, Detail, …) and *dimension* folders (Agents, Sessions, …). They used
// to share identical chrome — a 10px dash vs. a 10px chevron — so the list
// read as one flat menu. Two cues now separate them:
//   1. Group headers — a worded "Views" / "Break down by" eyebrow at the root
//      scope, a hairline divider once nested (NAV_GROUP_LABELS tunes this).
//   2. Per-lens glyphs — each lens kind gets its own icon, so the chevron is
//      now the unique "this folder opens" marker. Keyed by lens id, with a
//      Detail-page fallback (every value lens is titled "Detail"); callers that
//      pass no lensId (StudioMenu) keep the original dash.

// The view/breakdown split recurs at every scope, so the labels recur with it:
// "everywhere" stamps the worded eyebrows at each nested scope; "top-only"
// shows the words once at the root and lets the divider + glyphs carry it
// deeper. Everywhere is the default — the cue is needed wherever the split is.
const NAV_GROUP_LABELS = "everywhere";

const lensGroupLabel = (depth) =>
  NAV_GROUP_LABELS === "everywhere" || depth === 0 ? "Views" : null;
const dimGroupLabel = (depth) =>
  NAV_GROUP_LABELS === "everywhere" || depth === 0 ? "Break down by" : null;

// Inventory cube — shared by the endpoints override (dimension.endpoints) and
// the generic dimension inventory (dimension.inventory), so every "All <dim>"
// board reads with the same mark.
const INVENTORY_GLYPH = (
  <>
    <path d="m8 2 5.5 2.6v6L8 13.4 2.5 10.6v-6z" />
    <path d="m2.5 4.6 5.5 2.6 5.5-2.6M8 7.2v6.2" />
  </>
);

// 16×16 glyph paths, drawn in the same stroke family as the dimension chevron.
const LENS_GLYPHS = {
  "scope.overview": (
    <>
      <rect x="2.5" y="2.5" width="4.2" height="4.2" rx="1" />
      <rect x="9.3" y="2.5" width="4.2" height="4.2" rx="1" />
      <rect x="2.5" y="9.3" width="4.2" height="4.2" rx="1" />
      <rect x="9.3" y="9.3" width="4.2" height="4.2" rx="1" />
    </>
  ),
  "scope.xray": (
    <>
      <circle cx="8" cy="8" r="2.2" />
      <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2" />
    </>
  ),
  // Same cube for all three inventory ids: the scope lens (experimental off) and
  // the two dimension forms (endpoints override + generic default, experimental on).
  "scope.inventory": INVENTORY_GLYPH,
  "dimension.endpoints": INVENTORY_GLYPH,
  "dimension.inventory": INVENTORY_GLYPH,
  "scope.canvases": (
    <>
      <rect x="2.2" y="3.2" width="11.6" height="9.6" rx="1.4" />
      <path d="M2.2 6.4h11.6M6.2 6.4v6.4" />
    </>
  ),
  "dimension.signals": (
    <>
      <path d="M8 2.3a3.1 3.1 0 0 0-3.1 3.1c0 3.3-1.3 4.3-1.3 4.3h8.8s-1.3-1-1.3-4.3A3.1 3.1 0 0 0 8 2.3z" />
      <path d="M6.9 12.6a1.3 1.3 0 0 0 2.2 0" />
    </>
  ),
  "dimension.memories": (
    <>
      <path d="M2.6 4.6h10.8v2.6H2.6z" />
      <path d="M3.6 7.2v5.4h8.8V7.2" />
      <path d="M6.6 9.7h2.8" />
    </>
  ),
  // Anatomy — stacked, narrowing bars: the scope cascade (tenant → you).
  "scope.anatomy": (
    <>
      <rect x="2.4" y="3" width="11.2" height="2.3" rx="0.7" />
      <rect x="2.4" y="6.85" width="7.6" height="2.3" rx="0.7" />
      <rect x="2.4" y="10.7" width="4.4" height="2.3" rx="0.7" />
    </>
  ),
  // Reviews -- a shield with a check: this traffic was examined and judged.
  // Same idiom as the trace panel's Review tab.
  "scope.reviews": (
    <>
      <path d="M8 2 3 4v4c0 3 2.1 5.1 5 6 2.9-.9 5-3 5-6V4L8 2Z" />
      <path d="m6 8 1.5 1.5L10.3 6.6" />
    </>
  ),
  // Every value lens is titled "Detail": a folded-corner page.
  _detail: (
    <>
      <path d="M4 2.4h4.8l3.2 3.2v8H4z" />
      <path d="M8.6 2.4v3.4h3.4" />
      <path d="M6 8.8h4M6 11h4" />
    </>
  ),
  // No lens id supplied (e.g. StudioMenu) → the original dash.
  _dash: <path d="M4 8h8" />,
};

function LensGlyph({ lensId, active }) {
  const glyph =
    lensId == null ? LENS_GLYPHS._dash : LENS_GLYPHS[lensId] || LENS_GLYPHS._detail;
  return (
    <span
      className={[
        "grid h-[14px] w-[14px] place-items-center [&_svg]:block [&_svg]:h-[13px] [&_svg]:w-[13px]",
        active ? "text-[color:var(--green)]" : "text-[color:var(--muted-soft)]",
      ].join(" ")}
      aria-hidden="true"
    >
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        {glyph}
      </svg>
    </span>
  );
}

// Worded group eyebrow (e.g. "Views"). Reuses the shared EYEBROW_CLASS so it
// matches every other eyebrow in the app (the scope-hero kicker, panel
// section labels); only the row-content edge padding is layout-local.
export function NavSectionLabel({ children }) {
  return (
    <div
      className={`${EYEBROW_CLASS} select-none pl-[6px] pr-[9px] pb-[6px] pt-[6px]`}
      aria-hidden="true"
    >
      {children}
    </div>
  );
}

export function NavDivider() {
  return <div className="ml-[6px] mr-[9px] my-[6px] h-px bg-[color:var(--line-soft)]" aria-hidden="true" />;
}

// Header above a level's dimension folders: a divider off the lens group above,
// plus the worded eyebrow at the root scope. Only rendered when lenses sit
// above (so an overview-suppressed embed shows a bare folder list, no orphan).
function DimGroupHeader({ depth }) {
  const label = dimGroupLabel(depth);
  return (
    <>
      <NavDivider />
      {label ? <NavSectionLabel>{label}</NavSectionLabel> : null}
    </>
  );
}

// ── Dimension folder header ───────────────────────────────────────
//
// Exported so other sidebar surfaces (StudioMenu, etc.) can reuse the
// chevron + label + count chrome without re-implementing it. Pure UI
// — no data hooks — so it can live anywhere in the app.

// The ACCUMULATED applied steer for the level a pane sits at (query string) —
// every steer on the drill path above it, ANDed. Each ScopeLevel overrides this
// for its own children, so a pane's count/entries reflect exactly the steers in
// effect at its scope. Empty = no steer.
const SteerContext = createContext("");

// Per-level steer STATE, threaded once from the root: the prefix-keyed map, the
// setter and the per-prefix value-suggest factory. Rides
// a context rather than drilling through every recursive ScopeLevel.
const SteerStateContext = createContext(null);

// Stable empty array for the root level's scope prefix (avoids a fresh [] each
// render churning the steer suggest factory / memo deps).
const EMPTY_SCOPE = [];

// Accumulated steer for the scope prefix of length `len` (root..that level):
// AND of every level's steer on the path. Pure.
function accumulatedSteer(steers, scopePath, len) {
  if (!steers) return "";
  const keys = Array.from({ length: len + 1 }, (_, L) => encodeScopePath(scopePath.slice(0, L)));
  return andQueriesAll(keys.map((k) => steers[k]));
}

export function DimHeader({ label, count, loading, active, expanded, onClick, dimKey = null }) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...phAttrs({ [ATTRIBUTE.DIM]: dimKey })}
      className={[
        "relative grid min-h-[24px] w-full [grid-template-columns:14px_minmax(0,1fr)_auto] items-center gap-x-2 border-0 bg-transparent py-0 pl-[6px] pr-[9px] text-left text-[color:var(--muted)] transition-colors appearance-none cursor-pointer hover:text-[color:var(--ink)]",
        // Idle-hover wash only — the active/expanded green fill for the folder
        // is painted by the enclosing StickyFolder, so DimHeader doesn't add
        // its own (that would double-tint the resting active state).
        rowChromeClass(false, true),
        active || expanded ? "text-[color:var(--ink)]" : "",
      ].join(" ")}
    >
      <span
        className="grid place-items-center [&_svg]:block [&_svg]:h-[13px] [&_svg]:w-[13px]"
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={[
            "transition-transform",
            expanded ? "rotate-90" : "",
          ].join(" ")}
        >
          <path d="m6 3 5 5-5 5" />
        </svg>
      </span>
      <span className={ROOT_NAV_TITLE_CLASS}>{label}</span>
      {loading ? (
        <span
          className="h-[10px] w-[18px] justify-self-end rounded-[1px] bg-[color:var(--line-soft)] opacity-80"
          aria-label="Loading count"
        />
      ) : (
        <span className={ROOT_NAV_META_CLASS}>{count}</span>
      )}
    </button>
  );
}

// ── Overview row ───────────────────────────────────────────────
//
// Exported so other sidebars (StudioMenu, etc.) get the exact same
// uppercase + letter-spaced label and green-bar active treatment.
// Caller supplies `active` and `onClick`; defaults to "Overview"
// label but is overridable.
export function NavOverviewRow({ label = "Overview", active, onClick, lensId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "relative grid min-h-[24px] w-full [grid-template-columns:14px_minmax(0,1fr)_auto] items-center gap-x-2 border-0 bg-transparent py-[6px] pl-[6px] pr-[9px] text-left text-[color:var(--muted)] transition-colors appearance-none cursor-pointer hover:text-[color:var(--ink)]",
        rowChromeClass(active, true),
        active ? "text-[color:var(--ink)]" : "",
      ].join(" ")}
    >
      <LensGlyph lensId={lensId} active={active} />
      <span className={ROOT_NAV_TITLE_CLASS}>{label}</span>
      <span className="text-[11px] leading-[1.08] text-[color:var(--muted-soft)]" />
    </button>
  );
}

// A lens group: the "Views" eyebrow (gated by NAV_GROUP_LABELS + labelDepth)
// above one glyph row per lens. Shared by the root scope and every nested value
// so the two render sites can't drift; the caller owns what selecting a lens
// does (which scope to commit). labelDepth is the scope level the eyebrow
// describes — 0 is the absolute root.
function LensGroup({ lenses, activeId, labelDepth, onSelect }) {
  const label = lensGroupLabel(labelDepth);
  return (
    <>
      {label ? <NavSectionLabel>{label}</NavSectionLabel> : null}
      {lenses.map((l) => (
        <NavOverviewRow
          key={l.id}
          label={l.title || "Overview"}
          active={l.id === activeId}
          lensId={l.id}
          onClick={() => onSelect(l.id)}
        />
      ))}
    </>
  );
}

// ── Sub-content rendered inside a selected entry ──────────────────
// No longer filters traces — child ScopeLevel queries the worker
// with a deeper parentScopePath that includes this entry.

function SelectedSubContent({ scopePath, depth, onChange, onFocusDim, focusDim, view, onView, cacheRef }) {
  // Lens links for this selected value: the scope overview ("Overview") plus
  // any custom value lens (e.g. "Content compare"). Clicking one sets the
  // active `view` for this value; the recursive ScopeLevel below handles
  // drilling deeper (each click adds another {dim,id} step).
  const valueScope = scopePath.slice(0, depth + 1);
  const lenses = appLenses.resolveLenses({ scopePath: valueScope });
  // Overview first, custom lenses after.
  const links = [...lenses].sort((a, b) => (a.level === "scope" ? 0 : 1) - (b.level === "scope" ? 0 : 1));
  // Only the terminal value's links reflect the active view; deeper drills own it.
  const isTerminal = scopePath.length === depth + 1;
  // A focused dimension lens (focusDim) owns the main view — so NO scope-lens
  // row (Overview/X-ray/Inventory) is active; the focused dim's own pane shows
  // active instead. Without this, view===null defaults the highlight to Overview
  // even while the center renders the memory store.
  const activeId = focusDim
    ? null
    : isTerminal
      ? ((view && lenses.some((l) => l.id === view)) ? view : defaultLensOf(lenses)?.id)
      : null;
  return (
    <FolderList>
      {/* Always at least one scope below the root, so labelDepth = depth + 1
          (never the absolute-root case). */}
      <LensGroup
        lenses={links}
        activeId={activeId}
        labelDepth={depth + 1}
        onSelect={(id) => { startTransition(() => onChange(valueScope)); onView?.(id); }}
      />
      {/* Steer THIS scope — refines this level (and accumulates onto deeper
          drills). Scoped to valueScope so its value autocomplete suggests
          within this scope, not the deepest selection. */}
      <SteerControl prefixScope={valueScope} />
      <ScopeLevel
        scopePath={scopePath}
        depth={depth + 1}
        onChange={onChange}
        onFocusDim={onFocusDim}
        focusDim={focusDim}
        view={view}
        onView={onView}
        cacheRef={cacheRef}
      />
    </FolderList>
  );
}

// ── A single dimension folder with entries ────────────────────────

export function DimensionPane({ dim, scopePath, depth, isActiveDim, selectedIds = [], onChange, onFocusDim, focusDim, view, onView, cacheRef, defaultOpen = false, prefetchedCount }) {
  const steerQuery = useContext(SteerContext);
  const caps = useMyPermissions();
  // The parent scope path is everything up to this depth
  const parentScopePath = useMemo(() => scopePath.slice(0, depth), [scopePath, depth]);
  // Include parent scope in cache key so state doesn't leak across navigations
  const scopePrefix = encodeScopePath(parentScopePath);
  const cacheKey = `scope:${scopePrefix}:${depth}:${dim.key}`;

  const [isOpen, setIsOpen] = useCachedState(cacheRef, cacheKey, "open", defaultOpen || null);
  const [searchQuery, setSearchQuery] = useCachedState(cacheRef, cacheKey, "search", "");
  const [sortMode, setSortMode] = useCachedState(cacheRef, cacheKey, "sort", dim.sortDefault);
  const [entryExpanded, setEntryExpanded] = useCachedState(cacheRef, cacheKey, "entryExp", {});

  // A focused dim (focusDim===this dim) auto-opens its pane and shows active, so
  // landing on a dimension lens from elsewhere (e.g. the memory roster) reveals
  // where you are in the sidebar instead of leaving it collapsed under Overview.
  const effectiveOpen = isOpen !== null ? isOpen : (isActiveDim || focusDim === dim.key || defaultOpen);

  // For hierarchical dims that already appear shallower in the scope, hide the
  // pane entirely when it has nothing left to drill into. Avoids empty
  // "files: 0" folders nested inside a leaf file.
  const sameDimUsedShallower = parentScopePath.some((s) => s.dim === dim.key);

  // Header count: the parent ScopeLevel batch-prefetches counts for
  // every dim at this level in one query; this hook is the fallback
  // for when the prefetch hasn't arrived yet (or after open, when the
  // user has indicated interest). Gated on `effectiveOpen` so closed
  // panes don't fire their own count when the batched one is in
  // flight or already resolved.
  const range = useCurrentRange();
  const { data: entryCount, loading: countLoading } = useQuery(
    "getDimensionCount",
    { dimKey: dim.key, parentScopePath, range, query: steerQuery },
    { enabled: effectiveOpen },
  );
  // The batched prefetch is the source of truth: it's scoped through the same
  // prompt graph as everything else, so a signal in the parent scope flows into
  // the count via the SignalTags join (no special case). The per-pane entryCount
  // is the fallback before the batch arrives (or after open).
  // A null count = a steer this dim's count path can't carry yet (apiClient
  // compileSteer) → render "—" rather than a stale/unfiltered number. undefined
  // = still loading.
  const resolvedCount = (typeof prefetchedCount === "number" || prefetchedCount === null)
    ? prefetchedCount
    : entryCount;
  const hasCount = typeof resolvedCount === "number";

  // Full entries + sparklines paginated. The hook fetches page 0 when the
  // folder is open (or its parent scope changes), accumulates additional
  // pages on loadMore(), and persists page-0's max for share scaling.
  // A real 0 hides a reused hierarchical dim with nothing left to drill; a "—"
  // (null, steer can't carry) is NOT zero — keep the pane so the steer doesn't
  // silently drop dims it merely can't count yet.
  const shouldHide = dim.hierarchical && sameDimUsedShallower && !countLoading && resolvedCount === 0;
  const {
    entries,
    total: paginatedTotal,
    hasMore,
    loadMore,
    loading: entriesLoading,
    error: entriesError,
    initialLoad: pagedInitialLoad,
    revalidating: pagedRevalidating,
  } = usePagedDimEntries({
    dimKey: dim.key,
    parentScopePath,
    pageSize: 100,
    enabled: effectiveOpen && !shouldHide,
    search: searchQuery.trim(),
    sort: sortMode,
    steer: steerQuery,
  });
  const initialLoad = effectiveOpen && pagedInitialLoad;
  const revalidating = pagedRevalidating;
  // The signal catalog is separately permission-gated. Its main panel already
  // renders a typed denial; keep the matching sidebar facet from contradicting
  // that state with "No entries in scope" when no request was sent.
  const signalPermissionDenied = dim.key === "signals"
    && caps !== null
    && !hasPermission(caps, PERMISSIONS.SIGNALS_READ);
  const entriesErrorCopy = signalPermissionDenied
    ? denialCopy({ reason: DENIAL_REASON.PERMISSION_DENIED }, caps)
    : entriesError
      ? analyticsErrorCopy(entriesError, caps)
      : null;
  // Search and sort happen server-side now (the SQL applies them across the
  // full dim, not just the loaded pages). Endpoint rows additionally carry the
  // hardware serial and metadata snapshot time: collapse re-registrations with
  // the same rule as Endpoint Inventory, retaining the newest endpoint_id for
  // each real serial. Dedupe the accumulated raw pages here (not inside the
  // paging hook) so offsets and hasMore continue to follow server rows.
  const filteredEntries = useMemo(
    () => dim.key === "endpoints" ? applyEndpointDedupe(entries) : entries,
    [dim.key, entries],
  );
  // Once every raw page is loaded, the folder count can state the exact number
  // of visible physical endpoints. Until then retain the server count rather
  // than presenting a partial client-side count as complete.
  const endpointEntriesComplete = dim.key === "endpoints"
    && effectiveOpen
    && !hasMore
    && !entriesLoading
    && (entries.length > 0 || resolvedCount === 0);
  const visibleCount = endpointEntriesComplete
    ? filteredEntries.length
    : resolvedCount;
  const hasVisibleCount = typeof visibleCount === "number";
  // The entry read is the panel's authoritative availability signal. A count
  // can legitimately come from a cheaper, differently anchored query and say
  // zero while opening the actual panel is refused. Once that happens, keep the
  // header from continuing to advertise an empty tenant.
  const displayCount = entriesErrorCopy
    ? "Unavailable"
    : hasVisibleCount
      ? visibleCount
      : visibleCount === null
        ? "Unavailable"
        : "";

  useEffect(() => {
    if (!cacheRef?.current || !hasVisibleCount) return;
    if (!cacheRef.current[cacheKey]) cacheRef.current[cacheKey] = {};
    const existing = cacheRef.current[cacheKey]._columnSeed || {};
    cacheRef.current[cacheKey]._columnSeed = {
      ...existing,
      dimKey: dim.key,
      depth,
      parentScopeKey: scopePrefix,
      range,
      count: visibleCount,
    };
    cacheRef.current[CACHE_NOTIFY]?.();
  }, [cacheRef, cacheKey, dim.key, depth, scopePrefix, range, hasVisibleCount, visibleCount]);

  useEffect(() => {
    if (!cacheRef?.current || !effectiveOpen || initialLoad || !filteredEntries.length) return;
    if (!cacheRef.current[cacheKey]) cacheRef.current[cacheKey] = {};
    const existing = cacheRef.current[cacheKey]._columnSeed || {};
    cacheRef.current[cacheKey]._columnSeed = {
      ...existing,
      dimKey: dim.key,
      depth,
      parentScopeKey: scopePrefix,
      range,
      search: searchQuery.trim(),
      sort: sortMode,
      entries: filteredEntries,
      total: endpointEntriesComplete ? filteredEntries.length : paginatedTotal,
    };
    cacheRef.current[CACHE_NOTIFY]?.();
  }, [
    cacheRef,
    cacheKey,
    dim.key,
    depth,
    scopePrefix,
    range,
    effectiveOpen,
    initialLoad,
    filteredEntries,
    paginatedTotal,
    hasMore,
    endpointEntriesComplete,
    searchQuery,
    sortMode,
  ]);

  // Click semantics:
  //   plain click  → replace this depth's selection with [entryId]
  //   cmd/ctrl     → toggle entryId in the current dim's selection
  //                  (if not already at this depth, becomes the seed)
  //                  Disabled for non-multi-selectable dims.
  //   click on the sole selected entry  → toggle expand of its sub-content
  function handleEntryClick(entryId, event) {
    const isAlreadySelected = selectedIds.includes(entryId);
    const wantsAdditive = !!event && (event.metaKey || event.ctrlKey) && canMultiSelect(dim.key);

    if (wantsAdditive) {
      // Toggle within the current dim's id list. If we're not currently
      // scoped at this depth on this dim, seed the selection at depth.
      const currentSelectionMatchesThisDim = isActiveDim && selectedIds.length > 0;
      let nextIds;
      if (currentSelectionMatchesThisDim) {
        nextIds = isAlreadySelected
          ? selectedIds.filter((id) => id !== entryId)
          : [...selectedIds, entryId];
      } else {
        nextIds = [entryId];
      }
      if (nextIds.length === 0) {
        // De-selected last id — pop this step entirely.
        onChange(scopePath.slice(0, depth));
      } else {
        setIsOpen(true);
        setEntryExpanded((prev) => ({ ...prev, [entryId]: true }));
        onChange([
          ...scopePath.slice(0, depth),
          { dim: dim.key, ids: nextIds, id: nextIds[0] },
        ]);
      }
      return;
    }

    if (isAlreadySelected && selectedIds.length === 1) {
      setEntryExpanded((prev) => ({
        ...prev,
        [entryId]: prev[entryId] === false,
      }));
      return;
    }
    setIsOpen(true);
    setEntryExpanded((prev) => ({ ...prev, [entryId]: true }));
    onChange([
      ...scopePath.slice(0, depth),
      { dim: dim.key, ids: [entryId], id: entryId },
    ]);
  }

  const entryNodes = useMemo(
    () =>
      filteredEntries.map((entry) => {
        const isSelected = selectedIds.includes(entry.id);
        const isOnlySelected = isSelected && selectedIds.length === 1;
        const isExpanded = isOnlySelected && entryExpanded[entry.id] !== false;

        return {
          id: `${dim.key}:${entry.id}:${depth}`,
          // Dim-decorated title: icon + label (resolved through the dim
          // cache so raw enum forms like "1" prettify to "Claude") +
          // optional secondary line (e.g. endpoint registered_user).
          // Dims without these facets render only the label, visually
          // matching today's plain-text row.
          title: (
            <span className="inline-flex items-center gap-1 max-w-full align-middle">
              <DimensionIcon dim={dim.key} id={entry.id} className="h-2.5 w-2.5 shrink-0" />
              <DimensionLabel
                dim={dim.key}
                id={entry.id}
                fallback={entry.label}
                className="min-w-0 truncate"
              />
              <DimensionSecondary
                dim={dim.key}
                id={entry.id}
                className="min-w-0 truncate text-[11px] font-normal text-[color:var(--muted-soft)]"
              />
              {/* Inline status badge (e.g. signals' "disabled") — a short tag kept
                  on the title line, distinct from `note` (the two-row secondary
                  line below, still supported and passed through). Guarded by
                  entry.badge, so dims that don't set one add nothing. */}
              {entry.badge ? (
                <span className="shrink-0 text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted-soft)]">
                  {entry.badge}
                </span>
              ) : null}
            </span>
          ),
          note: entry.note,
          meta: (Number(entry.activity) || 0) >= 1000 ? formatCompact(Number(entry.activity)) : entry.activity,
          sparkline: entry.sparkline,
          // Lazy sparkline: when the dim's discovery query can't
          // cheaply carry bucket data (e.g. mcp_servers — see
          // executeMcpServerDimensionEntries), entries arrive with
          // `sparkline.series === null` as the lazy sentinel. The
          // runtime fires getDimEntrySparkline once the row scrolls
          // into view, gated by IntersectionObserver — so only the
          // visible page pays the per-row query cost.
          sparklineLoader: entry.sparkline?.series === null ? {
            dimKey: dim.key,
            id: entry.id,
            parentScopePath,
            range,
          } : null,
          share: entry.share,
          active: isSelected,
          open: isExpanded,
          sticky: isSelected && isExpanded,
          onClick: (event) => handleEntryClick(entry.id, event),
          onToggleExpand: () => handleEntryClick(entry.id, null),
          // Sub-pane (drill into next dim) only makes sense when ONE row
          // is selected — when multi-selected, scope is a union and the
          // child picker should appear at the next path depth instead.
          content: isOnlySelected ? (
            <SelectedSubContent
              scopePath={scopePath}
              depth={depth}
              onChange={onChange}
              onFocusDim={onFocusDim}
              focusDim={focusDim}
              view={view}
              onView={onView}
              cacheRef={cacheRef}
            />
          ) : null,
        };
      }),
    [filteredEntries, selectedIds, entryExpanded, dim, scopePath, depth, onChange, onFocusDim, focusDim, view, onView, cacheRef, isActiveDim],
  );

  if (shouldHide) return null;

  return (
    <StickyFolder
      open={effectiveOpen}
      active={isActiveDim || focusDim === dim.key}
      padded
      header={
        <DimHeader
          dimKey={dim.key}
          label={dim.label}
          count={displayCount}
          loading={countLoading && !hasCount}
          active={isActiveDim || focusDim === dim.key}
          expanded={effectiveOpen}
          onClick={() => {
            const next = !effectiveOpen;
            setIsOpen(next);
            // Opening a folder at the trailing scope level (where the
            // next drill-down happens) is the "expand Gemini → Clusters"
            // gesture. Record it as the most-recent trailing expansion so
            // the column view lands on the same dimension instead of
            // resetting to no trailing column and forcing a re-click +
            // refetch.
            if (next && depth === scopePath.length) markTrailExpansion(cacheRef, cacheKey);
            // Dims with a resolvable DIMENSION LENS (its own — SIGNALS,
            // ENDPOINTS — or the generic inventory default) focus their aggregate
            // board on header click: the dimension-level view, distinct from
            // selecting a specific value (which opens that value's lens). Engaging
            // a dim WITHOUT a dimension lens clears any focused lens, so the main
            // view stops showing a stale board while you browse.
            const lensDim = appLenses.dimensionLensFor(dim.key) ? dim.key : null;
            onFocusDim?.(lensDim);
            // Focusing a dim lens must clear a stale scope-view selection
            // (Overview/X-ray/Inventory), or that view keeps overriding the
            // dimension lens and the center stays on Overview. Symmetric to a
            // scope-view click, which clears focus via onChange→updateScopePath.
            if (lensDim) onView?.(null);
          }}
        />
      }
      toolbar={
        <FolderToolbar
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder={`Filter ${dimensionSentenceLabel(dim)}`}
          sortValue={sortMode}
          onSortChange={setSortMode}
          sorts={dim.sorts}
        />
      }
    >
      {appLenses.dimensionLensFor(dim.key) ? (
        // Lens-bearing dims (SIGNALS, ENDPOINTS, and — with the generic default
        // — every regular dim) get an explicit aggregate entry at the top of the
        // pane: focus the dimension lens (the all-<dim> board) and drop any
        // selected value, so it's a discoverable "back to the aggregate" distinct
        // from the per-value detail below. It's the dimension's overview lens, so
        // it carries that lens's own glyph + a hairline beneath — the same "lens
        // above, entries below" rhythm as a scope.
        <>
          <NavOverviewRow
            label={`All ${dimensionSentenceLabel(dim)}`}
            active={focusDim === dim.key}
            lensId={appLenses.dimensionLensFor(dim.key)?.id}
            onClick={() => { onChange(parentScopePath); onFocusDim?.(dim.key); onView?.(null); }}
          />
          <NavDivider />
        </>
      ) : null}
      {entriesErrorCopy ? (
        <div
          role="status"
          className="px-[10px] py-[9px] text-[11px] leading-[1.4] text-[color:var(--muted)]"
        >
          {entriesErrorCopy.message}
        </div>
      ) : initialLoad ? (
        <LoadingSkeleton rows={4} />
      ) : (
        <div
          style={{
            opacity: revalidating ? 0.4 : 1,
            transition: "opacity 180ms ease",
            pointerEvents: revalidating ? "none" : undefined,
          }}
        >
          <PaginatedExplorerContent
            nodes={entryNodes}
            // Search now runs server-side across the full dim, so the
            // returned `total` already reflects the searched dim
            // population (in the searched-page sense — the server caps
            // at LIMIT entries that satisfy the search). Load-more
            // works the same way regardless of search.
            grandTotal={endpointEntriesComplete ? filteredEntries.length : paginatedTotal}
            onLoadMore={hasMore ? loadMore : null}
            loadingMore={entriesLoading && !pagedInitialLoad}
            maxAutoLoads={dim.key === "endpoints" ? 1 : Infinity}
            cacheRef={cacheRef}
            cacheKey={cacheKey}
            stateKey="visible"
            resetKey={`${scopePrefix}|${searchQuery}|${sortMode}`}
            emptyMessage={searchQuery ? "No results match this filter." : "No entries in scope."}
          />
        </div>
      )}
    </StickyFolder>
  );
}

// ── Recursive scope level ─────────────────────────────────────────

function ScopeLevel({ scopePath, depth, onChange, onFocusDim, focusDim, view, onView, cacheRef, precededByLenses = true }) {
  // Dims already committed at a shallower depth in this scope chain.
  const used = useMemo(() => {
    const s = new Set();
    for (let i = 0; i < depth; i++) {
      const step = scopePath[i];
      if (step) s.add(step.dim);
    }
    return s;
  }, [scopePath, depth]);

  // The Memories dim is per-tenant (backend `memory` signal, deploy_to: none) with
  // no resource_paths fallback, so hide it until we confirm this tenant actually
  // has memory data — otherwise it renders empty. One shared probe query
  // (getMemorySignalAvailable), only when the dim could show at all (experimental
  // flag → DIMENSIONS.memories present). `data` is undefined until it resolves →
  // memories stays hidden; self-corrects on next load once the signal is deployed.
  const { data: memoriesAvailable } = useQuery(
    "getMemorySignalAvailable",
    {},
    { enabled: Boolean(DIMENSIONS.memories) },
  );

  const available = useMemo(() => {
    // Non-hierarchical dims drop out once they've been used at any shallower
    // depth (you can only pick one agent / endpoint / etc. per chain).
    // Hierarchical dims (files, clusters, projects) reappear so you can
    // drill deeper — DimensionPane hides itself if there are no children
    // at this position.
    //
    // Inside a project scope the file tree IS the project's hierarchy,
    // so the standalone files dim would just shadow what projects is
    // already rendering. Hide it.
    return DIMENSION_LIST.filter((d) => {
      if (!passesTenantAvailability(d.key, memoriesAvailable)) return false; // memories: hidden until its per-tenant signal is confirmed
      if (d.key === "files" && used.has("projects")) return false;
      return d.hierarchical || !used.has(d.key);
    });
  }, [used, memoriesAvailable]);

  const step = scopePath[depth];

  // Pre-fetch counts for every dim at this level in a single batched
  // analytics query. ~10 dim panes each used to fire their own
  // `getDimensionCount` on mount; the batched form returns all counts
  // in one row, so the sidebar header strip can show
  // "AGENTS 21 / ENDPOINTS 55 / CLUSTERS 30 / …" without N round trips.
  // Individual `getDimensionCount` calls inside DimensionPane stay
  // gated on `effectiveOpen` (the deeper-pane fallback).
  const parentScopePath = useMemo(() => scopePath.slice(0, depth), [scopePath, depth]);
  const range = useCurrentRange();
  // Accumulated steer for THIS level (root → depth), ANDed — drives this level's
  // batched counts and is provided to its panes for their per-pane queries.
  const steerState = useContext(SteerStateContext);
  const accSteer = useMemo(
    () => accumulatedSteer(steerState?.steers, scopePath, depth),
    [steerState?.steers, scopePath, depth],
  );
  const dimKeys = useMemo(() => available.map((d) => d.key), [available]);
  const { data: prefetchedCounts } = useQuery(
    "getDimensionCounts",
    { dimKeys, parentScopePath, range, query: accSteer },
    { enabled: dimKeys.length > 0 },
  );

  // A pane self-hides only when it's a hierarchical dim already used shallower
  // with nothing left to drill (DimensionPane.shouldHide). Every other dim
  // always renders, so the group header is guaranteed a folder beneath it —
  // gate on that so "Break down by" can never orphan above an empty level.
  const hasGuaranteedPane = available.some((d) => !(d.hierarchical && used.has(d.key)));

  return (
    <SteerContext.Provider value={accSteer}>
      {precededByLenses && hasGuaranteedPane ? <DimGroupHeader depth={depth} /> : null}
      {available.map((dim) => (
        <DimensionPane
          key={dim.key}
          dim={dim}
          scopePath={scopePath}
          depth={depth}
          isActiveDim={step?.dim === dim.key}
          selectedIds={step?.dim === dim.key ? stepIds(step) : []}
          onChange={onChange}
          onFocusDim={onFocusDim}
          focusDim={focusDim}
          view={view}
          onView={onView}
          cacheRef={cacheRef}
          defaultOpen={false}
          prefetchedCount={prefetchedCounts?.[dim.key]}
        />
      ))}
    </SteerContext.Provider>
  );
}

// ── Scope steer ───────────────────────────────────────────────────
//
// A compact trigger (not an inline editor) that opens the FilterBuilder
// popout, showing the active steer as an inline summary when collapsed. Reuses
// the same nested-group builder as the query bar, restricted to STEER_FIELDS.
// Steer-summary view mode: "chips" (structured, prominent — doubles as a clear
// "scope is steered" signal) vs "plain" (one compact monospace line). Backed by
// localStorage so it persists + flips live with no reload; toggle from the
// console: `steerSummary('plain')` / `steerSummary('chips')`. Default: chips.
const STEER_SUMMARY_KEY = "dashboard:steerSummary";
const _steerSummaryListeners = new Set();
function getSteerSummaryMode() {
  try { return localStorage.getItem(STEER_SUMMARY_KEY) === "plain" ? "plain" : "chips"; } catch { return "chips"; }
}
function setSteerSummaryMode(mode) {
  try { localStorage.setItem(STEER_SUMMARY_KEY, mode === "plain" ? "plain" : "chips"); } catch { /* ignore */ }
  _steerSummaryListeners.forEach((l) => l());
}
function subscribeSteerSummary(cb) { _steerSummaryListeners.add(cb); return () => _steerSummaryListeners.delete(cb); }
if (typeof window !== "undefined") window.steerSummary = setSteerSummaryMode;

// Render the active steer as field·op·value chips (matching the query-bar pill
// language) instead of raw text. The summary walks the AST by node type: each
// top-level conjunct is a chip; an OR/AND group is a boxed run of child chips
// with a green OR/AND separator (NOT one mis-parsed leaf — a serialized group
// like `endpoint:X OR model:Y` must never be regex-split as a single clause).
const STEER_CLAUSE_RE = /^(-?)([A-Za-z_][\w-]*)(:|!=|>=|<=|>|<)([\s\S]*)$/;
const STEER_OP_SYM = { ":": ":", "!=": "≠", ">=": "≥", "<=": "≤", ">": ">", "<": "<" };

// Top-level conjuncts of a steer (the implicit-AND children, or the single node).
function steerConjuncts(q) {
  const ast = parseQuery(q).ast;
  if (!ast) return [];
  return ast.type === "and" ? ast.children : [ast];
}

// One leaf clause (pred or negated pred) → a field·op·value chip. Serializes the
// node first, so the regex only ever sees a single clause (never a group).
function SteerLeafChip({ node }) {
  const m = STEER_CLAUSE_RE.exec(serializeQuery(node));
  // A multi-value IN list can carry hundreds of ids (the Topic patterns
  // session steer) — summarize to first-value +N and clamp the value's
  // width, or one giant nowrap chip widens the whole sidebar's
  // min-content and breaks the rail layout.
  let value = m ? m[4] : "";
  let more = 0;
  if (value.includes(",")) {
    const parts = value.split(",");
    more = parts.length - 1;
    value = parts[0];
  }
  return (
    <span className="inline-flex h-[17px] min-w-0 max-w-full items-center gap-[2px] whitespace-nowrap rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[5px] font-mono text-[11px] leading-none">
      {m ? (
        <>
          {m[1] ? <span className="font-semibold text-[color:var(--ink)]">−</span> : null}
          <span className="text-[color:var(--green)]">{m[2]}</span>
          <span className="text-[color:var(--muted)]">{STEER_OP_SYM[m[3]] || m[3]}</span>
          <span className="min-w-0 max-w-[140px] truncate text-[color:var(--ink)]">{value}</span>
          {more > 0 ? <span className="shrink-0 text-[color:var(--muted)]">+{more}</span> : null}
        </>
      ) : (
        <span className="min-w-0 truncate text-[color:var(--ink)]">{serializeQuery(node)}</span>
      )}
    </span>
  );
}

// Recursive: a group renders its children boxed with a green OR/AND separator; a
// leaf renders as a chip. Mirrors the query-bar's pill + OR-pill language.
function SteerClause({ node }) {
  if (!node) return null;
  if (node.type === "and" || node.type === "or") {
    const sep = node.type === "or" ? "OR" : "AND";
    return (
      <span className="inline-flex flex-wrap items-center gap-[3px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[3px] py-[2px]">
        {node.children.map((c, i) => (
          <Fragment key={i}>
            {i > 0 ? <span className="px-[1px] text-[10px] font-semibold leading-none text-[color:var(--green)]">{sep}</span> : null}
            <SteerClause node={c} />
          </Fragment>
        ))}
      </span>
    );
  }
  return <SteerLeafChip node={node} />;
}

function SteerControl({ prefixScope }) {
  const st = useContext(SteerStateContext);
  const summaryMode = useSyncExternalStore(subscribeSteerSummary, getSteerSummaryMode, () => "chips");
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const key = encodeScopePath(prefixScope);
  const steerQuery = st?.steers?.[key] || "";
  const q = steerQuery.trim();
  const active = q.length > 0;
  const onSteerChange = (t) => st?.setSteer?.(key, t);
  const suggestValues = st?.makeSteerSuggest ? st.makeSteerSuggest(prefixScope) : undefined;
  // Hidden when steering is disabled (no setter provided).
  if (!st || typeof st.setSteer !== "function") return null;
  return (
    <div className="relative">
      {/* Flush row, same chrome/gutter as a dimension header — the filter glyph
          sits where the dim chevrons do, the summary where their labels do. */}
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={active ? `Steer: ${q.length > 160 ? `${q.slice(0, 160)}…` : q}` : "Steer this scope"}
        className={[
          // items-start + small vertical padding so chips can WRAP to multiple
          // lines (the leading glyph + trailing ✕ stay aligned to the first row).
          "relative flex min-h-[24px] w-full items-start gap-2 border-0 bg-transparent py-[3px] pl-[6px] pr-[9px] text-left transition-colors appearance-none cursor-pointer [&_svg]:block",
          rowChromeClass(false, true),
          open || active ? "text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]",
        ].join(" ")}
      >
        <span className="grid h-[17px] shrink-0 place-items-center [&_svg]:h-[13px] [&_svg]:w-[13px]" aria-hidden="true">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className={active ? "text-[color:var(--green)]" : ""}>
            <path d="M2 4h12M4 8h8M6.5 12h3" />
          </svg>
        </span>
        {/* Match the row labels' type. Placeholder takes the full nav-title
            treatment (uppercase/tracked); an active steer keeps the same size +
            weight + family but stays normal-case so the query reads faithfully. */}
        {active ? (
          summaryMode === "plain" ? (
            <span className="min-w-0 flex-1 self-center truncate font-mono text-[11px] leading-none text-[color:var(--ink)]">{q}</span>
          ) : (
            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-[4px]">
              {steerConjuncts(q).map((n, i) => <SteerClause key={i} node={n} />)}
            </span>
          )
        ) : (
          <span className={`min-w-0 flex-1 truncate ${ROOT_NAV_TITLE_CLASS} text-[color:var(--muted-soft)] self-center`}>Steer this scope…</span>
        )}
        {active ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear steer"
            onClick={(e) => { e.stopPropagation(); onSteerChange(""); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onSteerChange(""); } }}
            className="grid h-[17px] w-[16px] shrink-0 place-items-center text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] [&_svg]:h-3 [&_svg]:w-3"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M4 4l8 8M12 4l-8 8" /></svg>
          </span>
        ) : null}
      </button>
      {open ? (
        <FilterBuilder
          query={steerQuery}
          suggestValues={suggestValues}
          fields={STEER_FIELDS}
          anchorRef={btnRef}
          onApply={(nq) => onSteerChange(nq)}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

// ── Root component ────────────────────────────────────────────────

export default memo(function ScopeSidebar({ scopePath, onChange, onFocusDim, focusDim, view, onView, hideOverview = false, cacheRef: providedCacheRef = null, steers = null, setSteer = null, makeSteerSuggest = null }) {
  // App.jsx normally owns the cache so it can mirror state into the
  // URL ?ui= param for deep linking; the local fallback is only for
  // standalone usage (tests / storybook).
  const localCacheRef = useRef({});
  const cacheRef = providedCacheRef ?? localCacheRef;
  // Per-level steer state, shared with every recursive level via context.
  const steerStateValue = useMemo(
    () => ({ steers: steers || {}, setSteer, makeSteerSuggest }),
    [steers, setSteer, makeSteerSuggest],
  );

  return (
    <SteerStateContext.Provider value={steerStateValue}>
    <nav
      className="flex flex-col flex-auto py-1 min-h-0 transition-[max-height,opacity,transform] duration-[240ms,180ms,240ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] [&>:last-child]:pb-3"
      data-nav
    >
      {!hideOverview && (() => {
        // Root lens links — same pattern as SelectedSubContent's per-value
        // links: every scope-level lens (Overview + universal companions
        // like Inventory) gets a row; clicking selects that view at the
        // root scope. Active state only applies when the root IS the
        // current scope.
        const rootLenses = appLenses.resolveLenses({ scopePath: [] });
        // A focused dimension lens owns the view — no root scope-lens row is
        // active (the focused dim's own pane shows active instead).
        const activeId = (!focusDim && scopePath.length === 0)
          ? ((view && rootLenses.some((l) => l.id === view)) ? view : defaultLensOf(rootLenses)?.id)
          : null;
        return (
          <LensGroup
            lenses={rootLenses}
            activeId={activeId}
            labelDepth={0}
            onSelect={(id) => { startTransition(() => onChange([])); onView?.(id); }}
          />
        );
      })()}
      {/* Root scope steer — refines the root breakdown (and accumulates onto
          deeper drills). Self-hides when steering is disabled. Each ScopeLevel
          provides its own accumulated SteerContext for its panes. */}
      <SteerControl prefixScope={EMPTY_SCOPE} />
      <ScopeLevel
        scopePath={scopePath}
        depth={0}
        onChange={onChange}
        onFocusDim={onFocusDim}
        focusDim={focusDim}
        view={view}
        onView={onView}
        cacheRef={cacheRef}
        precededByLenses={!hideOverview}
      />
    </nav>
    </SteerStateContext.Provider>
  );
});
